import { showNewMessage } from '@intercom/messenger-js-sdk';
import axios from 'axios';
import { useAuthStore } from 'src/entities/user/store/useAuthStore';
import { BACKEND_URL as BASE_URL } from 'src/settings';

import { addToast, HeroButton as Button, cn } from 'src/shared/components/ui';

const shownToasts = new Set<string>();
let isHandlingUnauthorized = false;
const DEACTIVATED_USER_DETAIL = 'User is deactivated';

const handleUnauthorizedLogout = (): void => {
  if (isHandlingUnauthorized) return;

  isHandlingUnauthorized = true;
  useAuthStore.getState().logout();
  window.location.href = '/login';
};

function showToastOnce(key: string, toastConfig: any) {
  if (shownToasts.has(key)) return;

  addToast({
    ...toastConfig,
    classNames: {
      base: cn([
        'shadow-sm',
        'rounded-md',
        'flex flex-col items-start',
        'border-danger-200 dark:border-danger-100 border-l-danger text-danger-500'
      ]),
      icon: 'w-6 h-6 fill-current'
    },
    endContent: (
      <div className="ms-11 my-2 flex gap-x-2">
        <Button
          onPress={() => {
            localStorage.clear();
            window.location.href = '/login';
          }}
          color="primary"
          size="sm"
        >
          Login again
        </Button>
        <Button
          onPress={() => {
            showNewMessage('I cannot access my account');
          }}
          className="bg-white"
          color="primary"
          size="sm"
          variant="bordered"
        >
          Contact us
        </Button>
      </div>
    ),
    timeout: 999999,
    color: 'danger'
  });
  shownToasts.add(key);
}

const apiService = axios.create({
  baseURL: `${BASE_URL}/api`,
  headers: { 'Content-Type': 'application/json' }
});

apiService.interceptors.request.use(
  (config) => {
    const { authToken, selectedOrganization, impersonation } =
      useAuthStore.getState();

    if (authToken) {
      config.headers.Authorization = `Token ${authToken}`;
    }

    if (selectedOrganization && selectedOrganization.id) {
      config.headers['X-Organization-Id'] = selectedOrganization.id.toString();
    }

    if (impersonation && impersonation !== '0') {
      config.headers['Impersonation'] = impersonation;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiService.interceptors.response.use(
  (response) => response,
  async (error) => {
    const detail = error.response?.data?.detail;
    const detailMessage = detail ? ` Error: ${detail} ` : '';

    switch (error.response?.status) {
      case 401:
        showToastOnce('401', {
          title: 'You are not authorized to access this.',
          color: 'danger'
        });
        break;
      case 403:
        if (detail === DEACTIVATED_USER_DETAIL) {
          handleUnauthorizedLogout();
        } else {
          showToastOnce('403', {
            title: 'You are not authorized to access this.',
            color: 'danger'
          });
        }
        break;
      case 500:
        showToastOnce('500', {
          title: `Oops, an error occurred. We're working on a fix.${detailMessage}`,
          color: 'danger'
        });
        break;

      default:
        // Network errors (error.code === 'ERR_NETWORK') are handled by the
        // QueryClient: it retries them transparently and only toasts once
        // via the global QueryCache/MutationCache onError handler.
        break;
    }

    return Promise.reject(error);
  }
);

export default apiService;
